import {
  FACTOR_LABELS,
  maxScore,
  type CharacterSummary,
  type FateMode,
  type LegacySummary,
  type RankedCharacter,
  type TimerRow,
  type TonightResponse,
} from '@brimstone/shared';
import { useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { DataGrid, EmptyState, KpiTile, PageLayout, Panel, Tag } from '../../components/ui';
import { WhereWasIPanel } from '../../components/WhereWasIPanel';
import { formatMoney } from '../../lib/money';
import { raceAndClass, useRoll, useTonight, useUpdateCharacter } from '../../lib/roster';
import { formatAge, formatDuration, formatPlayed, useNow } from '../../lib/time';
import { ShowOnMap, characterSpot } from '../../components/ShowOnMap';

const COLUMNS =
  '36px minmax(0, 1.5fr) 48px minmax(0, 1.2fr) minmax(0, 1.7fr) 72px minmax(0, 1.5fr)';
/** A roll stays "tonight's pick" this long. */
const PICK_HOURS = 12;

function Dice() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect
        x="2"
        y="2"
        width="14"
        height="14"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="6" cy="6" r="1.3" fill="currentColor" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" />
      <circle cx="9" cy="9" r="1.3" fill="currentColor" />
    </svg>
  );
}

function ModeSwitch({ mode, onChange }: { mode: FateMode; onChange: (m: FateMode) => void }) {
  return (
    <div role="group" aria-label="Roll mode" className="flex border border-line">
      {(['weighted', 'uniform'] as const).map((m) => (
        <button
          key={m}
          type="button"
          aria-pressed={mode === m}
          onClick={() => onChange(m)}
          className={`min-h-11 cursor-pointer px-3 font-mono text-label uppercase ${
            mode === m ? 'bg-accent font-semibold text-bg' : 'text-muted hover:text-text'
          }`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

/** The 8 px stacked bar: rested, legacy, away, then everything else. */
function FactorBar({ c, max }: { c: RankedCharacter; max: number }) {
  const points = (key: string) => c.factors.find((f) => f.key === key)?.points ?? 0;
  const other = c.factors
    .filter((f) => !['rested', 'legacy', 'away'].includes(f.key))
    .reduce((s, f) => s + f.points, 0);
  const pct = (p: number) => `${max > 0 ? (p / max) * 100 : 0}%`;
  return (
    <span className="flex h-2 flex-1 bg-line-soft" aria-hidden="true">
      <span className="block h-2 bg-accent" style={{ width: pct(points('rested')) }} />
      <span className="block h-2 bg-legacy" style={{ width: pct(points('legacy')) }} />
      <span className="block h-2 bg-away" style={{ width: pct(points('away')) }} />
      <span className="block h-2 bg-other" style={{ width: pct(other) }} />
    </span>
  );
}

function RestedCell({ c }: { c: RankedCharacter }) {
  if (!c.rested) return <span className="font-mono text-secondary text-muted">no data</span>;
  if (c.rested.atMaxLevel)
    return <span className="font-mono text-secondary text-muted">max level</span>;
  const full = c.rested.levels >= c.rested.capLevels - 0.005;
  return (
    <span className="flex flex-col gap-1.5">
      <span className="font-mono text-secondary">
        {c.rested.levels.toFixed(2)} lv{full ? ' · full' : ''}
      </span>
      <span className="block h-1 bg-line">
        <span
          className="block h-1 bg-accent"
          style={{ width: `${Math.min(1, c.rested.levels / 1.5) * 100}%` }}
        />
      </span>
    </span>
  );
}

function legacyText(c: RankedCharacter): string {
  const m = c.nextLegacy;
  if (!m) return c.level === null ? 'no data' : 'nothing left';
  const unit = m.unit === 'level' ? (m.distance === 1 ? 'level' : 'levels') : 'skill';
  return `${m.label} · ${m.distance} ${unit}`;
}

function Breakdown({ c }: { c: RankedCharacter }) {
  const update = useUpdateCharacter();
  return (
    <div
      className="border-b border-line-soft bg-bg px-4 py-3"
      role="region"
      aria-label={`${c.name}’s score`}
    >
      <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,2fr)_56px_64px] gap-x-3.5 gap-y-1 text-secondary">
        <span className="text-label uppercase tracking-label text-muted">Factor</span>
        <span className="text-label uppercase tracking-label text-muted">From</span>
        <span className="text-right text-label uppercase tracking-label text-muted">Weight</span>
        <span className="text-right text-label uppercase tracking-label text-muted">Points</span>
        {c.factors.map((f) => (
          <span key={f.key} className="contents">
            <span>{FACTOR_LABELS[f.key]}</span>
            <span className="min-w-0 truncate text-muted">
              {f.noData ? <span className="font-mono">NO DATA</span> : f.detail}
            </span>
            <span className="text-right font-mono text-muted">{f.weight}</span>
            <span className="text-right font-mono">{f.points.toFixed(1)}</span>
          </span>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          <Link to={`/book/${c.id}`} className="text-secondary">
            {c.name}’s Book page
          </Link>
          <ShowOnMap link={characterSpot(c.id, c.zone)} character={c.id} />
        </span>
        <button
          type="button"
          disabled={update.isPending}
          onClick={() => update.mutate({ id: c.id, inTonightPool: false })}
          className="min-h-11 cursor-pointer px-2 text-secondary text-muted hover:text-text"
        >
          Take out of tonight’s pool
        </button>
      </div>
    </div>
  );
}

function RankedRow({ c, max, picked }: { c: RankedCharacter; max: number; picked: boolean }) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };
  return (
    <>
      <div
        role="row"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onKey}
        className={`grid cursor-pointer items-center gap-x-3.5 border-b border-line-soft px-4 py-3 hover:bg-line-soft ${picked ? 'bg-accent-row' : ''}`}
        style={{ gridTemplateColumns: COLUMNS }}
      >
        <span role="cell" className="font-mono text-muted">
          {String(c.rank).padStart(2, '0')}
        </span>
        <span role="cell" className="min-w-0">
          <span className="flex items-center gap-2 text-name font-semibold">
            <span className="truncate">{c.name}</span>
            {picked && <Tag>FATE</Tag>}
          </span>
          <span className="block text-secondary text-muted">{raceAndClass(c)}</span>
        </span>
        <span role="cell" className="font-mono">
          {c.level ?? '—'}
        </span>
        <span role="cell">
          <RestedCell c={c} />
        </span>
        <span role="cell" className="text-compact">
          {legacyText(c)}
        </span>
        <span role="cell" className="font-mono text-secondary text-muted">
          {c.lastPlayedAt ? formatAge(c.lastPlayedAt).replace(' ago', '') : '—'}
        </span>
        <span role="cell" className="flex items-center gap-2.5">
          <span className="w-8 font-mono text-score font-semibold">{c.score}</span>
          <FactorBar c={c} max={max} />
        </span>
      </div>
      {open && <Breakdown c={c} />}
    </>
  );
}

function names(cs: CharacterSummary[]) {
  return cs.map((c, i) => (
    <span key={c.id}>
      {i > 0 && ', '}
      <Link to={`/book/${c.id}`} className="text-text no-underline hover:text-accent">
        {c.name}
      </Link>
      {c.level !== null && (
        <span className="text-muted">
          {' '}
          {c.level} {raceAndClass(c)}
        </span>
      )}
    </span>
  ));
}

/** Legacy (SPEC §6.2): progress to the account rewards and the three nearest milestones. */
function LegacyPanel({ legacy }: { legacy: LegacySummary }) {
  const bounds = [0, ...legacy.rewards, legacy.total];
  return (
    <Panel
      title="Legacy"
      level="side"
      meta={legacy.nextReward ? `next reward @ ${legacy.nextReward}` : 'all rewards'}
    >
      <div className="px-4 py-3.5">
        <div
          className="flex h-2.5 gap-0.5"
          aria-label={`${legacy.earned} of ${legacy.total} points`}
        >
          {bounds.slice(1).map((end, i) => {
            const start = bounds[i]!;
            const fill = Math.min(1, Math.max(0, (legacy.earned - start) / (end - start)));
            return (
              <span key={end} className="flex bg-line" style={{ flex: end - start }}>
                <span className="block bg-accent" style={{ width: `${fill * 100}%` }} />
              </span>
            );
          })}
        </div>
        <div className="mt-1.5 flex gap-0.5 font-mono text-tag text-muted">
          {bounds.slice(1).map((end, i) => (
            <span key={end} className="text-right" style={{ flex: end - bounds[i]! }}>
              {end}
            </span>
          ))}
        </div>
        <p className="m-0 mt-2 font-mono text-secondary">
          {legacy.earned}
          {legacy.capped ? '+' : ''}
          <span className="text-muted">
            {' '}
            / {legacy.total} {legacy.source === 'computed' ? '(counted)' : ''}
          </span>
        </p>
        {legacy.closest.length > 0 ? (
          <ul className="m-0 mt-3 flex list-none flex-col gap-1.5 p-0 text-compact">
            {legacy.closest.map((m) => (
              <li key={m.label} className="flex justify-between gap-2.5">
                <span>
                  {m.characterName} · {m.label}
                </span>
                <span className="font-mono text-muted">
                  {m.distance} {m.unit === 'level' ? 'lvl' : 'skill'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 mt-3 text-secondary text-muted">No milestone in reach yet.</p>
        )}
      </div>
    </Panel>
  );
}

const TIMERS_SHOWN = 6;

function TimersPanel({ timers, now }: { timers: TimerRow[]; now: number }) {
  const [all, setAll] = useState(false);
  const shown = all ? timers : timers.slice(0, TIMERS_SHOWN);
  return (
    <Panel title="Timers" level="side" meta={timers.length ? String(timers.length) : undefined}>
      {timers.length === 0 ? (
        <EmptyState>No timers yet: they appear once characters have logged out.</EmptyState>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 px-4 py-3 text-compact">
          {shown.map((t) => {
            const left = new Date(t.readyAt).getTime() - now;
            return (
              <li
                key={`${t.kind}${t.characterId}${t.label}`}
                className="flex justify-between gap-2.5"
              >
                <span className="min-w-0 truncate">
                  {t.characterName} · {t.label}{' '}
                  <ShowOnMap link={t.map} character={t.characterId} label="map" />
                </span>
                <span className={`shrink-0 font-mono ${left <= 0 ? 'text-accent' : 'text-muted'}`}>
                  {left <= 0
                    ? t.kind === 'rested'
                      ? 'FULL'
                      : 'READY'
                    : `in ${formatDuration(left)}`}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {timers.length > TIMERS_SHOWN && (
        <button
          type="button"
          onClick={() => setAll((a) => !a)}
          className="min-h-11 w-full cursor-pointer border-0 border-t border-line bg-transparent px-4 text-left text-compact text-accent"
        >
          {all ? 'Show fewer' : `Show all ${timers.length}`}
        </button>
      )}
    </Panel>
  );
}

function Kpis({ t }: { t: TonightResponse }) {
  const k = t.kpis;
  const gold = k.netWorthCopper === null ? '—' : formatMoney(k.netWorthCopper, true);
  const played = k.playedThisWeekSeconds === null ? '—' : formatPlayed(k.playedThisWeekSeconds);
  return (
    <>
      <KpiTile
        label="Legacy"
        value={k.legacyEarned === null ? '—' : `${k.legacyEarned}${k.legacyCapped ? '+' : ''}`}
        unit={`/${k.legacyTotal}`}
      />
      <KpiTile label="Net worth" value={gold} />
      <KpiTile label="Played this week" value={played} />
      <KpiTile
        label="Explored"
        value={k.exploredPercent === null ? '—' : `${Math.round(k.exploredPercent)}%`}
      />
    </>
  );
}

/** Tonight (SPEC §6.4): who to play now, why, and a fate roll. */
export function TonightPage() {
  const tonight = useTonight();
  const now = useNow();
  const roll = useRoll();
  const update = useUpdateCharacter();
  const [mode, setMode] = useState<FateMode>('weighted');
  const t = tonight.data;

  const latest = roll.data?.roll ?? t?.rolls[0];
  const fresh = latest && now - new Date(latest.at).getTime() < PICK_HOURS * 3_600_000;
  const pickId = fresh ? latest.pickedId : null;
  const whereId = pickId ?? t?.ranked[0]?.id ?? null;
  const max = t ? maxScore(t.weights) : 0;

  return (
    <PageLayout
      kpis={t && <Kpis t={t} />}
      main={
        <Panel
          title={
            <span className="flex flex-col">
              <span>Tonight</span>
              <span className="text-secondary font-normal text-muted">
                Score = rested + Legacy + away + more · <Link to="/settings#weights">Weights</Link>
              </span>
            </span>
          }
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-secondary text-muted" aria-live="polite">
                {roll.isPending ? 'rolling…' : fresh ? `> ${latest.pickedName}` : `${mode} roll`}
              </span>
              <ModeSwitch mode={mode} onChange={setMode} />
              <button
                type="button"
                disabled={roll.isPending || !t?.ranked.length}
                onClick={() => roll.mutate(mode)}
                className="inline-flex min-h-11 cursor-pointer items-center gap-2 border border-accent bg-transparent px-4 font-mono text-compact font-semibold text-accent disabled:cursor-default disabled:opacity-50"
              >
                <Dice />
                Let fate decide
              </button>
            </div>
          }
        >
          <div className="flex flex-wrap gap-x-4.5 gap-y-1.5 border-b border-line px-4 py-2.5 text-label text-muted">
            {(
              [
                ['bg-accent', 'Rested'],
                ['bg-legacy', 'Legacy'],
                ['bg-away', 'Away'],
                ['bg-other', 'Other'],
              ] as const
            ).map(([color, label]) => (
              <span key={label} className="flex items-center gap-1.5">
                <span className={`block size-2.5 ${color}`} />
                {label}
              </span>
            ))}
            <span className="ml-auto">Tap a row for its breakdown</span>
          </div>
          {roll.isError && (
            <p className="m-0 px-4 py-2 text-secondary text-danger">{roll.error.message}</p>
          )}
          {!t ? (
            <EmptyState>{tonight.isError ? 'Couldn’t load Tonight.' : 'Loading…'}</EmptyState>
          ) : t.ranked.length === 0 ? (
            <EmptyState>
              Nobody is in tonight’s pool. Add characters below or on their Book page.
            </EmptyState>
          ) : (
            <DataGrid
              label="Tonight’s ranking"
              columns={COLUMNS}
              header={['#', 'Character', 'Lvl', 'Rested', 'Next Legacy', 'Away', 'Score']}
            >
              {t.ranked.map((c) => (
                <RankedRow key={c.id} c={c} max={max} picked={c.id === pickId} />
              ))}
            </DataGrid>
          )}
          {t && (
            <div className="flex flex-col gap-1.5 px-4 py-3 text-secondary text-muted">
              {t.duoOnly.length > 0 && <span>Duo only, excluded: {names(t.duoOnly)}</span>}
              {t.notCreated.length > 0 && <span>Not created: {names(t.notCreated)}</span>}
              {t.outOfPool.length > 0 && <span>Out of the pool: {names(t.outOfPool)}</span>}
              {[...t.outOfPool, ...t.notCreated].length > 0 && (
                <span className="flex flex-wrap items-center gap-x-2">
                  Add to the pool:
                  {[...t.outOfPool, ...t.notCreated].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ id: c.id, inTonightPool: true })}
                      className="min-h-11 cursor-pointer px-1 text-text hover:text-accent"
                    >
                      + {c.name}
                    </button>
                  ))}
                </span>
              )}
            </div>
          )}
        </Panel>
      }
      side={
        <>
          <WhereWasIPanel
            characterId={whereId}
            note={pickId ? 'fate’s pick' : whereId ? 'top of tonight' : undefined}
          />
          {t && <LegacyPanel legacy={t.legacy} />}
          {t && <TimersPanel timers={t.timers} now={now} />}
          {t && t.rolls.length > 0 && (
            <Panel title="Last rolls" level="side" meta={`${t.rolls.length} of 5`}>
              <ul className="m-0 flex list-none flex-col gap-1.5 px-4 py-3 text-compact">
                {t.rolls.map((r) => (
                  <li key={r.id} className="flex justify-between gap-3">
                    <span>
                      {r.pickedName} <span className="text-muted">of {r.pool.length}</span>
                    </span>
                    <span className="font-mono text-secondary text-muted">
                      {formatAge(r.at)} · {r.mode}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </>
      }
    />
  );
}
